<script setup lang="ts">
import { computed } from 'vue'
import type { CategorySection as Section } from '@/lib/group'
import { shortfall } from '@/lib/group'
import type { Finding } from '@/lib/usePolledScan'
import { categoryBlurb, categoryTitle } from '@/lib/tone'
import FindingCard from './FindingCard.vue'
import PassGroup from './PassGroup.vue'

const props = defineProps<{
  section: Section<Finding>
  summary?: { total: number; passed: number; failed: number; unknown: number } | null
  describe?: (checkId: string) => string | undefined
  openPasses?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:openPasses', value: boolean): void
}>()

// The summary is complete and unfiltered. Falling back to what this client
// actually rendered only happens when the caller has no summary to give.
const counts = computed(() => props.summary ?? props.section.rendered)
const gap = computed(() => shortfall(props.section.rendered.total, counts.value.total))
const headingId = computed(() => `${props.section.anchor}-heading`)

function onTogglePasses(value: boolean) {
  emit('update:openPasses', value)
}
</script>

<template>
  <section
    :id="section.anchor"
    class="scroll-mt-24"
    :aria-labelledby="headingId"
  >
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <h2
          :id="headingId"
          class="text-balance text-xl font-semibold text-ink"
        >
          {{ categoryTitle(section.category) }}
        </h2>
        <p class="text-sm text-ink-muted">
          {{ categoryBlurb(section.category) }}
        </p>
      </div>
      <p class="tabular text-sm text-ink-muted">
        {{ counts.total }} checks · {{ counts.failed }} failed · {{ counts.unknown }} unknown
        <!-- Appears when the list is short of the summary, which today means the
             server capped it and one day may mean a viewer is not entitled to
             all of it. Same comparison either way. -->
        <span v-if="gap > 0"> · showing {{ section.rendered.total }} of {{ counts.total }}</span>
      </p>
    </div>

    <TransitionGroup
      name="finding"
      tag="div"
      class="mt-4 flex flex-col gap-3"
    >
      <template
        v-for="row in section.rows"
        :key="row.id"
      >
        <FindingCard
          v-if="row.kind === 'finding'"
          :finding="row.finding"
          :description="describe?.(row.finding.checkId)"
        />
        <PassGroup
          v-else
          :findings="row.findings"
          :open="openPasses"
          @update:open="onTogglePasses"
        />
      </template>
    </TransitionGroup>
  </section>
</template>
